"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { EASE } from "@/lib/motion";
import { PlatformError } from "@/lib/api/client";
import { useDebounced, useGameBrowse } from "@/lib/api/hooks";
import type { toGames } from "@/lib/api/mappers";
import { messageFor } from "@/lib/api/messages";
import { Icon, type IconName } from "@/components/ui/Icon";
import { SectionEmpty, SectionError, TileSkeleton } from "@/components/ui/states";
import { useContent } from "@/components/shell/ContentContext";
import { ALL_STUDIOS, type BrowseCategory, type BrowseStudio } from "./GameBrowser";
import { GameTile } from "./GameTile";

/* The kit's category page (desktop-category-page / mobile-category-page):
   the category pills as navigation, the title with its game count, the
   search bar and provider selector, then every game in the category as a
   grid that pages with "Load more". */

export type CategoryFirstPage = {
  data: Parameters<typeof toGames>[0];
  total: number;
  nextCursor: string | null;
};

/* The pills are links, not filters: each category is its own page. The
   first is the casino lobby, as the kit draws it, unless it is switched off.
   `activeSlug` is the category page being shown; `lobbyActive` lights the
   Lobby pill when the row sits on the lobby itself. */
export function CategoryPills({
  lobbyHref,
  lobbyActive = false,
  categories,
  activeSlug,
}: {
  lobbyHref: string | null;
  lobbyActive?: boolean;
  categories: BrowseCategory[];
  activeSlug: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLAnchorElement>(null);

  /* On a phone the active pill can sit past the edge; bring it into view
     without scrolling the page itself. A pill already in view stays put, so
     the row keeps opening on the Lobby pill. */
  useEffect(() => {
    const track = trackRef.current;
    const active = activeRef.current;
    if (!track || !active) return;

    const start = active.offsetLeft;
    const end = start + active.offsetWidth;
    if (start >= track.scrollLeft && end <= track.scrollLeft + track.clientWidth) return;

    track.scrollLeft = start - (track.clientWidth - active.offsetWidth) / 2;
  }, [activeSlug]);

  /* Feature flags -> Category row style: the kit's pills, or tabs — an
     icon over its label, the lit one in the brand colour and underlined. */
  const tabs = useContent().flags.categoryStyle === "Tabs";
  const pill = tabs
    ? "relative flex min-w-[76px] shrink-0 flex-col items-center gap-[6px] rounded-lg px-[10px] pb-[10px] pt-[8px] text-[11px] font-bold leading-[1.2] transition-colors duration-200"
    : "flex h-[32px] shrink-0 items-center gap-[4px] rounded-rounded px-[16px] text-[12px] font-bold leading-[1.6] transition-colors duration-200";
  const idle = tabs ? "text-fg-subtle hover:bg-elevation-2 hover:text-fg" : "bg-elevation-2 text-fg-subtle hover:bg-elevation-3 hover:text-fg";
  const lit = tabs ? "text-brand-bold" : "bg-brand text-fg-on-primary";
  const iconSize = tabs ? 24 : 14;
  const underline = <span aria-hidden className="absolute inset-x-[12px] bottom-0 h-[3px] rounded-t-full bg-brand" />;

  return (
    <nav aria-label="Casino categories">
      <div
        ref={trackRef}
        className={cn(
          "scrollbar-none relative flex items-center overflow-x-auto overflow-y-hidden",
          tabs ? "gap-[4px] border-b border-border" : "gap-[8px]",
        )}
      >
        {lobbyHref && (
          <Link
            href={lobbyHref}
            aria-current={lobbyActive ? "page" : undefined}
            className={cn(pill, lobbyActive ? lit : idle)}
          >
            <Icon name="grid" size={iconSize} />
            Lobby
            {tabs && lobbyActive && underline}
          </Link>
        )}
        {categories.map((category) => {
          const active = category.slug === activeSlug;

          return (
            <Link
              key={category.slug}
              ref={active ? activeRef : undefined}
              href={`/c/${category.slug}`}
              aria-current={active ? "page" : undefined}
              className={cn(pill, active ? lit : idle)}
            >
              <Icon name={category.icon} size={iconSize} />
              <span className={cn(tabs && "whitespace-nowrap")}>{category.name}</span>
              {tabs && active && underline}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

const FIELD =
  "flex h-[40px] items-center gap-[8px] rounded-rounded border border-border bg-elevation-1 px-[20px] transition-colors duration-200";

export function CategoryBrowser({
  categoryId,
  title,
  icon,
  total,
  studios,
  initialStudioSlug = "all",
  initialQuery = "",
  pageSize,
  firstPage,
  search = true,
  providers = true,
  heading = "h1",
}: {
  categoryId: string | null;
  title: string;
  icon: IconName;
  total: number | null;
  studios: BrowseStudio[];
  initialStudioSlug?: string;
  /* A search carried in from elsewhere (the top bar's field, as ?q=). */
  initialQuery?: string;
  /* The same size the server read its first page at, so pages line up. */
  pageSize: number;
  firstPage: CategoryFirstPage | null;
  search?: boolean;
  providers?: boolean;
  /* h1 on the category page, h2 wherever the page has a title of its own. */
  heading?: "h1" | "h2";
}) {
  const initialStudio = studios.find((studio) => studio.slug === initialStudioSlug) ?? ALL_STUDIOS;
  const [studio, setStudio] = useState(initialStudio);
  const [query, setQuery] = useState(initialQuery);
  const debouncedQuery = useDebounced(query.trim());
  const filtered = studio.id !== null || debouncedQuery.length > 0;
  const Heading = heading;

  /* The server already read the first page; hand it to the browse query so
     the grid paints with the page and "Load more" continues from its cursor
     instead of asking for the same games again. The key is useGameBrowse's
     own — if it ever drifts, the grid simply loads on the client. */
  const client = useQueryClient();
  useState(() => {
    if (!firstPage || initialStudio.id) return;

    const key = ["games", categoryId ?? "all", "", []];
    if (!client.getQueryData(key)) {
      client.setQueryData(key, { pages: [firstPage], pageParams: [undefined] });
    }
  });

  const browse = useGameBrowse({
    categoryId,
    query: debouncedQuery,
    studioIds: studio.id ? [studio.id] : undefined,
    limit: pageSize,
  });

  const chooseStudio = (next: BrowseStudio) => {
    setStudio(next);

    /* Keep the choice in the address so a shared link opens on it. */
    const url = new URL(window.location.href);
    if (next.id) url.searchParams.set("studio", next.slug);
    else url.searchParams.delete("studio");
    window.history.replaceState(window.history.state, "", url);
  };

  return (
    <div className="flex flex-col gap-[16px] lg:gap-[12px]">
      <header className="flex min-h-[32px] items-center gap-[8px]">
        <Icon name={icon} size={20} className="shrink-0 text-brand-bold" />
        <Heading className="truncate text-[17.5px] font-extrabold capitalize leading-[1.15] lg:text-[22px]">{title}</Heading>
        {total !== null && (
          <span className="inline-flex h-[18px] shrink-0 items-center rounded-rounded border border-brand-bold px-[6px] text-[9px] font-bold leading-[1.5] text-fg">
            {total} {total === 1 ? "Game" : "Games"}
          </span>
        )}
      </header>

      {(search || (providers && studios.length > 0)) && (
      <div className="flex flex-col gap-[10px] md:flex-row">
        {search && (
        <label className={cn(FIELD, "min-w-0 focus-within:border-brand md:flex-1")}>
          <Icon name="search" size={16} className="shrink-0 text-fg-subtle" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Search ${title}`}
            aria-label={`Search ${title}`}
            className="h-full min-w-0 flex-1 bg-transparent text-[12px] font-bold leading-[1.6] text-fg outline-none placeholder:text-fg-subtle"
          />
          {browse.isFetching && !browse.isFetchingNextPage && filtered && (
            <span className="h-[14px] w-[14px] shrink-0 animate-spin rounded-full border-2 border-fg-disabled border-t-brand-bold" />
          )}
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="shrink-0 text-fg-subtle transition-colors hover:text-fg"
            >
              <Icon name="close" size={16} />
            </button>
          )}
        </label>
        )}

        {providers && studios.length > 0 && (
          <ProviderSelect studios={studios} value={studio} onChange={chooseStudio} />
        )}
      </div>
      )}

      <Results browse={browse} filtered={filtered} pageSize={pageSize} />
    </div>
  );
}

function ProviderSelect({
  studios,
  value,
  onChange,
}: {
  studios: BrowseStudio[];
  value: BrowseStudio;
  onChange: (studio: BrowseStudio) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const outside = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);

    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative md:w-[250px] md:shrink-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={cn(FIELD, "w-full hover:border-elevation-4", open && "border-brand")}
      >
        <Icon name="layers" size={16} className="shrink-0 text-fg-subtle" />
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-left text-[12px] font-bold leading-[1.6]",
            value.id ? "text-fg" : "text-fg-subtle",
          )}
        >
          {value.id ? value.name : "Providers"}
        </span>
        <Icon
          name="chevron-down"
          size={16}
          className={cn("shrink-0 text-fg-subtle transition-transform duration-200", open && "rotate-180")}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+8px)] z-30 flex max-h-[320px] w-full flex-col gap-[2px] overflow-y-auto rounded-xl border border-border bg-elevation-1 p-[8px] shadow-[0_4px_4px_rgba(0,0,0,0.25)]"
        >
          {[ALL_STUDIOS, ...studios].map((option) => {
            const selected = option.slug === value.slug;

            return (
              <button
                key={option.slug}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
                className={cn(
                  "flex h-[36px] shrink-0 items-center justify-between gap-[8px] rounded-lg px-[12px] text-left text-[12px] font-bold leading-[1.6] transition-colors duration-200",
                  selected ? "bg-elevation-2 text-fg" : "text-fg-subtle hover:bg-elevation-2 hover:text-fg",
                )}
              >
                <span className="truncate">{option.id ? option.name : "All Providers"}</span>
                {selected && <Icon name="check" size={14} className="shrink-0 text-brand-bold" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const GRID =
  "grid grid-cols-3 gap-[8px] sm:[grid-template-columns:repeat(auto-fill,minmax(120px,1fr))] lg:gap-[12px]";

function Results({
  browse,
  filtered,
  pageSize,
}: {
  browse: ReturnType<typeof useGameBrowse>;
  filtered: boolean;
  pageSize: number;
}) {
  if (browse.isPending) {
    return (
      <div className={GRID}>
        {Array.from({ length: 16 }, (_, index) => (
          <TileSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (browse.isError) {
    const error =
      browse.error instanceof PlatformError ? browse.error : new PlatformError(0, "network", "Unknown failure.");

    return (
      <div className="rounded-2xl bg-elevation-1">
        <SectionError
          title="These games didn’t load"
          body={messageFor(error, "games")}
          action={
            <button
              type="button"
              onClick={() => browse.refetch()}
              className="h-9 rounded-lg bg-elevation-3 px-4 text-[12px] font-bold transition-colors hover:bg-elevation-4"
            >
              Try again
            </button>
          }
        />
      </div>
    );
  }

  if (browse.games.length === 0) {
    return (
      <div className="rounded-2xl bg-elevation-1">
        {filtered ? (
          <SectionEmpty
            icon="ghost"
            title="No games match that"
            body="Try a different search or provider, or clear them to see the whole category."
          />
        ) : (
          <SectionEmpty
            icon="slots"
            title="No games in this category"
            body="Games can also be hidden by the licence rules that apply to your location."
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[8px] lg:gap-[12px]">
      <div className={GRID}>
        {browse.games.map((game, index) => (
          <motion.div
            key={`${game.id}-${index}`}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, ease: EASE, delay: Math.min((index % pageSize) * 0.02, 0.3) }}
          >
            <GameTile game={game} />
          </motion.div>
        ))}
      </div>

      {browse.hasNextPage && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => browse.fetchNextPage()}
            disabled={browse.isFetchingNextPage}
            className="h-[38px] rounded-lg bg-elevation-2 px-[12px] text-[12px] font-bold leading-[1.6] text-fg transition-colors duration-200 hover:bg-elevation-3 disabled:opacity-60"
          >
            {browse.isFetchingNextPage ? "Loading…" : `Load more (${browse.games.length} of ${browse.total})`}
          </button>
        </div>
      )}
    </div>
  );
}

/* The category games' shape while the server reads the first page. */
export function CategoryGamesSkeleton() {
  return (
    <div className="flex flex-col gap-[16px] lg:gap-[12px]">
      <div className="h-[32px] w-[200px] max-w-full animate-pulse rounded-lg bg-elevation-1" />
      <div className="h-[40px] w-full animate-pulse rounded-rounded bg-elevation-1" />
      <div className={GRID}>
        {Array.from({ length: 16 }, (_, index) => (
          <TileSkeleton key={index} />
        ))}
      </div>
    </div>
  );
}
